import { Inject } from '@odinjs/odin';

import { ENDPOINTS } from '@philips-emr/hpms-health-insurance-commons';
import { catchRenderException, shouldRenderMobile } from '@philips-emr/hpms-health-insurance-components';
import { PanelAPI, TextAPI } from '@philips-emr/tws-components';
import { ThemeTonalRange } from '@philips-emr/tws-constants';
import Controller, { OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';

import { DiscreditedProviderCardInfoAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import './discredited-provider.scss';
import { GeneralParams } from './discredited-provider-controller';
import { ItemDetail } from './discredited-providers-modal-controller';
import { BasePath405, Icons405, IconsAccreditation, LocationIcon, mountDetailButton, mountIconField, mountIconsFor, PhoneIcon, SiteIcon } from './icons-button-utils';

@Controller({ fragment: FragmentNames.DISCREDITED_PROVIDER_CARD_INFO })
export default class DiscreditedProviderCardInfoController extends BaseController {

  private itemDetail: ItemDetail;
  private isMobile: boolean = false;
  private generalParams: GeneralParams;

  @Inject(Aliases.PANEL_CONTENT)
  private panelContent: PanelAPI;

  @OnMount(Aliases.PANEL_ROOT)
  onRootMount(): void {
    const nrSeqProvider = this.context.get('nrSeqProvider');
    this.panelContent.setVisible(false);
    this.generalParams = this.context.getBulkData() as GeneralParams;
    HttpService.get<ItemDetail>(ENDPOINTS.GET.DISCREDITED_PROVIDER_DETAIL, { nrSeqProvider }).then((response) => {
      if (response.body !== undefined) {
        this.itemDetail = response.body;
        this.context.set({ ...this.itemDetail });
      }
      this.panelContent.setVisible(true);
    }).catch(() => catchRenderException(() => {
      if (this.panelContent.isVisible()) {
        this.panelContent.setVisible(false);
      }
    }));
  }

  @OnMount(Aliases.PANEL_MOBILE_LINKS)
  onMountMobileLinks(event: MountEvent): void {
    const api = event.target as PanelAPI;
    if(shouldRenderMobile())api.setVisible(true);
  }

  @OnMount(Aliases.PANEL_DESKTOP_LINKS)
  onMountDesktopLinks(event: MountEvent): void {
    const api = event.target as PanelAPI;
    if(!shouldRenderMobile())api.setVisible(true);
  }

  @OnMount([
    Aliases.PANEL_COMPACT_NAME,
    Aliases.PANEL_DS_AREA,
    Aliases.PANEL_DS_EMAIL,
    Aliases.PANEL_DS_TYPE_PROVIDER,
    Aliases.PANEL_DS_SPECIALITY,
    Aliases.PANEL_DS_REASON,
  ])
  onPanelsMount(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const field = api.getAlias().replace('panel-', '');
    const value = this.context.get(field) as string;

    api.setVisible((value !== null && value !== undefined && value.trim().length > 0));

    if (this.generalParams === undefined) return;

    if (value === 'dsEmail' && 'S' !== this.generalParams.ieShowEmailDiscrProvider) {
      api.setVisible(false);
    }
  }

  @OnMount([
    Aliases.TEXT_COMPACT_NAME,
    Aliases.TEXT_DS_AREA,
    Aliases.TEXT_DS_EMAIL,
    Aliases.TEXT_DS_TYPE_PROVIDER,
    Aliases.TEXT_DS_SPECIALITY,
    Aliases.TEXT_DS_REASON,
  ])
  onTextsMount(event: MountEvent): void {
    const api = event.target as TextAPI;
    const field = api.getAlias().replace('text-', '');
    const value = this.context.get(field) as string;

    api.setValue(value);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_ICONS_D)
  onCustomContentRenderDetailIcons(event: RenderEvent): void {
    if (this.itemDetail === undefined) return;
    const { render } = event;
    mountIconsFor(
      render,
      this.itemDetail.legends,
      (this.context.get('legendsBasePath') === BasePath405 ? Icons405 : IconsAccreditation),
      this.context.get('legendsBasePath') as string
    );
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_BUTTON)
  onCustomContentRenderDetailButton(event: RenderEvent): void {
    if (!this.isMobile) return;
    const { render } = event;
    const nrSeqProvider = this.context.get('nrSeqProvider') as string;
    mountDetailButton(nrSeqProvider, this.context.get('legendsBasePath') as string, render);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_DS_ADDRESS)
  onCustomContentRenderAddress(event: RenderEvent): void {
    const { render } = event;
    const dsAddress = this.context.get('dsAddress') as string;
    if (dsAddress !== null && dsAddress !== undefined && dsAddress.trim().length > 0) {
      mountIconField(render, LocationIcon, dsAddress);
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_DS_PHONE)
  onCustomContentRenderPhone(event: RenderEvent): void {
    const { render } = event;
    const dsPhone = this.context.get('dsPhone') as string;
    if (dsPhone !== null && dsPhone !== undefined && dsPhone.trim().length > 0) {
      mountIconField(render, PhoneIcon, dsPhone);
    }    
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_DS_WEB_SITE)
  onCustomContentRenderSite(event: RenderEvent): void {
    const { render } = event;
    const dsWebSite = this.context.get('dsWebSite') as string;
    if (dsWebSite !== null && dsWebSite !== undefined && dsWebSite.trim().length > 0) {
      mountIconField(render, SiteIcon, dsWebSite);
    }
  }

  @OnClick([
    Aliases.DESKTOP_BUTTON_PRODUCTS,
    Aliases.MOBILE_BUTTON_PRODUCTS,
  ])
  onProductClick(): void {
    const nrSeqProvider = this.context.get('nrSeqProvider');
    ModalService.openDefault({
      closeable: true,
      fragment: FragmentNames.PRODUCT_LIST,
      title: 'hi.offered-products',
      contextData: { nrSeqProvider },
      theme: ThemeTonalRange.ULTRA_LIGHT,
    });
  }

  @OnClick([
    Aliases.DESKTOP_BUTTON_DS_ADDRESS_GMAPS,
    Aliases.MOBILE_BUTTON_DS_ADDRESS_GMAPS,
  ])
  onGmapsClick(): void {
    const nrSeqProvider = this.context.get('nrSeqProvider');
    ModalService.openFullscreen({
      closeable: true,
      fragment: FragmentNames.MAP_VIEW,
      contextData: { nrSeqProvider },
      theme: ThemeTonalRange.ULTRA_LIGHT,
    });
  }
}
